import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { useStaffAuth } from "../store/staff";

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/15";
const labelCls =
  "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500";

/** Staff reset-password — POST /api/staff/auth/reset-password ({ token, password }). */
const StaffResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const { staffResetPassword, staffAuthActionLoading, staffAuthActionError } = useStaffAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    if (!token) {
      setOk(false);
      setMsg("Missing reset token. Open the link from your reset email.");
      return;
    }
    if (password.length < 6) {
      setOk(false);
      setMsg("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setOk(false);
      setMsg("Passwords do not match.");
      return;
    }
    const res = await staffResetPassword({ token, password });
    if (res.success) {
      setOk(true);
      setMsg("Password reset successfully. Redirecting to sign in…");
      setTimeout(() => navigate("/staff/login"), 1500);
    } else {
      setOk(false);
      setMsg(res.error || "Failed to reset password.");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f5f7fa] px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[#16213A] text-white">
            <FontAwesomeIcon icon={faKey} className="text-xl" />
          </div>
          <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900">Reset Password</h1>
          <p className="m-0 mt-1 text-sm text-slate-500">Choose a new password for your staff account.</p>
        </div>

        {(msg || staffAuthActionError) && (
          <div
            className={`mb-4 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${
              ok ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-700"
            }`}
          >
            <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5" />
            <span>{msg || staffAuthActionError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>New Password</label>
            <input
              type="password"
              className={inputCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div>
            <label className={labelCls}>Confirm New Password</label>
            <input
              type="password"
              className={inputCls}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            disabled={staffAuthActionLoading}
            className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#16213A] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#203052] disabled:opacity-70"
          >
            {staffAuthActionLoading ? "Saving…" : "Reset Password"}
          </button>
        </form>

        <Link to="/staff/login" className="mt-4 block text-center text-xs font-semibold text-[#16213A] hover:underline">
          Back to sign in
        </Link>
      </div>
    </div>
  );
};

export default StaffResetPassword;
